import React from 'react';

import {
  AutocompleteItem,
  Checkbox,
  Divider,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import ClassCoachRoleAutoComplete, {
  ClassCoachRoleAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/ClassCoachRoleAutoComplete';
import ClassTypeAutoComplete, {
  ClassTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/ClassTypeAutoComplete';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/StaffAutoComplete';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs, { Dayjs } from 'dayjs';
import { RRuleSet, rrulestr } from 'rrule';
import { DayAbbreviation, KiloClassRetrieve } from 'types';
import { TimeUnitEnum } from 'types/enums';
import { getReservationAndCancellationSettings } from 'utils/event-json-settings.helpers';
import {
  calculateEventEndDateTime,
  getDayAbbreviationFromDate,
  getDayAbbreviationsFromRruleBydays,
  getDayDiff,
  parseRruleForFrequency,
} from 'utils/event.helpers';

interface FormValues {
  kiloClassId: string;
  startDate: Date;
  startTime: string;
  duration: number;
  endDate: Date | null;
  classCoachClassCoachRoles: {
    kiloClassCoachKiloClassCoachRoleId: string;
    coach: StaffAutoCompleteValue;
    classCoachRole: ClassCoachRoleAutoCompleteValue;
  }[];
  capacity: number;
  kiloClassType: ClassTypeAutoCompleteValue;
  location: LocationAutoCompleteValue;
  rootKiloClass: string | null;
  days: DayAbbreviation[];
  isRecurring: boolean;
  classEnd: Dayjs | null;
  dayDiff: string;
  waitlistLimit: number;
  reservationAndCancellationSettings: {
    reservationWindowOpenInterval: 'minute' | 'hour' | 'day';
    reservationWindowOpenIntervalCount: number;
    reservationWindowCloseInterval: 'minute' | 'hour' | 'day';
    reservationWindowCloseIntervalCount: number;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
    waitlistMax: number;
  };
}

interface IDetailsViewProps {
  kiloClass: KiloClassRetrieve;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: 'Min(s)' },
  { value: TimeUnitEnum.HOUR, label: 'Hour(s)' },
  { value: TimeUnitEnum.DAY, label: 'Day(s)' },
];

const DetailsView = ({ kiloClass }: IDetailsViewProps): JSX.Element => {
  const { country } = useLocalization();

  const [settingsOpened, { toggle: handleSettingsToggle }] = useDisclosure();

  const initialDays = kiloClass.rootKiloClass?.rrule
    ? getDayAbbreviationsFromRruleBydays(
        (rrulestr(kiloClass.rootKiloClass.rrule, { forceset: true }) as RRuleSet)._rrule[0].options
          .byweekday,
      )
    : [getDayAbbreviationFromDate(dayjs(kiloClass.startDate).toDate())];

  const isMonthlyRecurrence = parseRruleForFrequency(kiloClass.rootKiloClass?.rrule) === 'monthly';

  const initialClassEnd = calculateEventEndDateTime(
    kiloClass.duration,
    kiloClass.startTime,
    dayjs(kiloClass.startDate).toDate(),
  );

  const reservationAndCancellationSettings = getReservationAndCancellationSettings(
    kiloClass.reservationAndCancellationSettings,
  );

  const initialFormValues: FormValues = {
    ...kiloClass,
    startDate: dayjs(kiloClass.startDate).toDate(),
    startTime: dayjs(`${kiloClass.startDate} ${kiloClass.startTime}`).format('HH:mm').toString(),
    endDate: kiloClass.rootKiloClass?.endDate ? dayjs(kiloClass.rootKiloClass.endDate).toDate() : null,
    kiloClassType: {
      kiloClassTypeId: kiloClass.kiloClassType?.kiloClassTypeId || '',
      value: kiloClass.kiloClassType?.kiloClassTypeId || '',
      label: kiloClass.kiloClassType?.name || '',
    },
    classCoachClassCoachRoles: kiloClass.kiloClassCoachKiloClassCoachRoles.map(ccccr => ({
      kiloClassCoachKiloClassCoachRoleId: ccccr.kiloClassCoachKiloClassCoachRoleId,
      coach: {
        staffId: ccccr.coach.staffId,
        value: ccccr.coach.staffId,
        label: ccccr.coach.fullName,
      },
      classCoachRole: {
        kiloClassCoachRoleId: ccccr.kiloClassCoachRole.kiloClassCoachRoleId,
        value: ccccr.kiloClassCoachRole.kiloClassCoachRoleId,
        label: ccccr.kiloClassCoachRole.name,
      },
    })),
    location: {
      locationId: kiloClass.location?.locationId || '',
      label: kiloClass.location?.name || '',
      value: kiloClass.location?.locationId || '',
    },
    rootKiloClass: kiloClass.rootKiloClass?.kiloClassId || null,
    days: initialDays,
    isRecurring: !!kiloClass.rrule,
    waitlistLimit: kiloClass.waitlistLimit || 0,
    reservationAndCancellationSettings: reservationAndCancellationSettings,
    classEnd: initialClassEnd,
    dayDiff: getDayDiff(dayjs(kiloClass.startDate), initialClassEnd),
  };

  const form = useForm<FormValues>({
    initialValues: initialFormValues,
  });

  const handleCoachChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedCoach = {
      staffId: item ? item.staffId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`classCoachClassCoachRoles.${index}.coach`, selectedCoach);
  };

  const handleClassCoachRoleChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedClassCoachRole = {
      kiloClassCoachRoleId: item ? item.kiloClassCoachRoleId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`classCoachClassCoachRoles.${index}.classCoachRole`, selectedClassCoachRole);
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleClassTypeChange = (item: ClassTypeAutoCompleteValue | null): void => {
    const selectedKiloClassType = {
      kiloClassTypeId: item ? item.kiloClassTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('kiloClassType', selectedKiloClassType);
  };

  useShallowEffect(() => {
    form.setValues(initialFormValues);
  }, [kiloClass]);

  return (
    <Stack spacing='xs'>
      <Group grow align='top'>
        <ClassTypeAutoComplete
          label='Class Type'
          placeholder='Select a class type'
          value={form.values.kiloClassType}
          error={form.errors.kiloClassType}
          onChange={handleClassTypeChange}
          disabled
          data-cy='classTypeAutoCompleteRead'
        />
        <div />
      </Group>

      <Paper withBorder p='sm' bg='gray.0'>
        <Stack spacing='sm'>
          {!form.values.classCoachClassCoachRoles.length && (
            <Text size='sm' color='dimmed'>
              No Coach
            </Text>
          )}
          {form.values.classCoachClassCoachRoles.map((ccccr, i) => (
            <Group key={ccccr.kiloClassCoachKiloClassCoachRoleId} position='apart' align='flex-start' noWrap>
              <StaffAutoComplete
                label='Coach'
                placeholder='Select a coach'
                {...form.getInputProps(`classCoachClassCoachRoles.${i}.coach`)}
                onChange={value => handleCoachChange(value, i)}
                disinclude={form.values.classCoachClassCoachRoles.map(c => c.coach.staffId)}
                isCoach
                disabled
              />
              <ClassCoachRoleAutoComplete
                label='Role'
                placeholder='Select a role'
                {...form.getInputProps(`classCoachClassCoachRoles.${i}.classCoachRole`)}
                onChange={value => handleClassCoachRoleChange(value, i)}
                disabled
              />
              <div />
            </Group>
          ))}
        </Stack>
      </Paper>

      <Group grow align='top'>
        <LocationAutoComplete
          label='Location'
          placeholder='Select a location'
          value={form.values.location}
          onChange={handleLocationChange}
          error={form.errors.location}
          disabled
          data-cy='locationAutoCompleteRead'
        />
        <NumberInput
          {...form.getInputProps('capacity')}
          label='Capacity'
          placeholder='Enter class capacity'
          disabled
          data-cy='capacityInputRead'
        />
      </Group>

      <Divider my='xs' mb={0} mx='-xl' />

      <Group grow align='top'>
        <NumberInput
          {...form.getInputProps('duration')}
          label='Duration in minutes'
          placeholder='Enter duration'
          disabled
          data-cy='durationInputRead'
        />
        <div />
      </Group>

      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <DateInput {...form.getInputProps('startDate')} label='Start Date' locale='us' disabled />
        <Group spacing='xs' grow>
          <TimeInput
            label='From'
            {...form.getInputProps('startTime')}
            disabled
            data-cy='timeFromInputRead'
          />
          <TimeInput
            label={
              <Group position='apart'>
                <div>To</div>
                <Text color='red'>{form.values.dayDiff}</Text>
              </Group>
            }
            value={form.values.classEnd ? form.values.classEnd.format('HH:mm') : ''}
            disabled
          />
        </Group>
      </SimpleGrid>
      <Group pt='xs'>
        <Stack>
          <Group>
            <Checkbox checked={form.values.isRecurring} label='Re-occurring Event' disabled />
          </Group>

          <Stack pl='md'>
            {isMonthlyRecurrence ? (
              <Input.Wrapper label='Recurrence'>
                <Text size='sm'>Monthly — same date each calendar month</Text>
              </Input.Wrapper>
            ) : (
              <Checkbox.Group label='Weekly' {...form.getInputProps('days')}>
                <Group spacing='lg'>
                  <Checkbox data-test='sunday' value='SU' label='Sun' disabled />
                  <Checkbox data-test='monday' value='MO' label='Mon' disabled />
                  <Checkbox data-test='tuesday' value='TU' label='Tues' disabled />
                  <Checkbox data-test='wednesday' value='WE' label='Wed' disabled />
                  <Checkbox data-test='thursday' value='TH' label='Thu' disabled />
                  <Checkbox data-test='friday' value='FR' label='Fri' disabled />
                  <Checkbox data-test='saturday' value='SA' label='Sat' disabled />
                </Group>
              </Checkbox.Group>
            )}
            <Group grow align='top'>
              <DateInput
                {...form.getInputProps('endDate')}
                label='End Date'
                placeholder={form.values.isRecurring ? 'Never' : 'N/A'}
                data-test='endDate'
                disabled
              />
              <div />
            </Group>
          </Stack>
        </Stack>
      </Group>

      <Divider mt='lg' mb={0} mx='-xl' />

      <ExpandableSection
        title='Additional Settings'
        opened={settingsOpened}
        handleToggle={handleSettingsToggle}
      >
        <Stack spacing='sm' px='md'>
          <Input.Wrapper label='Waitlist capacity'>
            <SimpleGrid cols={2}>
              <NumberInput min={0} {...form.getInputProps('waitlistLimit')} disabled />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper label='The amount of time before a class when reservations will be accepted'>
            <SimpleGrid cols={2}>
              <NumberInput
                min={0}
                {...form.getInputProps(
                  'reservationAndCancellationSettings.reservationWindowOpenIntervalCount',
                )}
                disabled
              />
              <Select
                data={timeUnitOptions}
                {...form.getInputProps('reservationAndCancellationSettings.reservationWindowOpenInterval')}
                disabled
              />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper label='The amount of time before a class when reservations will no longer be accepted'>
            <SimpleGrid cols={2}>
              <NumberInput
                min={0}
                {...form.getInputProps(
                  'reservationAndCancellationSettings.reservationWindowCloseIntervalCount',
                )}
                disabled
              />
              <Select
                data={timeUnitOptions}
                {...form.getInputProps('reservationAndCancellationSettings.reservationWindowCloseInterval')}
                disabled
              />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper>
            <SimpleGrid cols={1}>
              <Switch
                my='xs'
                label='Allow late cancellations'
                checked={form.values.reservationAndCancellationSettings.allowLateCancellation}
                disabled
              />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper
            label={
              form.values.reservationAndCancellationSettings.allowLateCancellation
                ? 'Time before the start of a class when a cancellation is considered late'
                : 'Time before the start of a class when cancellations are no longer allowed'
            }
          >
            <SimpleGrid cols={2}>
              <NumberInput
                min={0}
                {...form.getInputProps('reservationAndCancellationSettings.cancellationIntervalCount')}
                disabled
              />
              <Select
                data={timeUnitOptions}
                {...form.getInputProps('reservationAndCancellationSettings.cancellationInterval')}
                disabled
              />
            </SimpleGrid>
          </Input.Wrapper>
          {form.values.reservationAndCancellationSettings.allowLateCancellation && (
            <>
              <Input.Wrapper label='Auto-Charge Fee for late cancellations'>
                <SimpleGrid cols={2}>
                  <MoneyField
                    country={country}
                    min={0}
                    {...form.getInputProps('reservationAndCancellationSettings.cancellationFee')}
                    disabled
                  />
                </SimpleGrid>
              </Input.Wrapper>

              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    label='Deduction of credit for late cancellation'
                    checked={
                      form.values?.reservationAndCancellationSettings?.cancellationCreditDeduction || false
                    }
                    disabled
                    {...form.getInputProps('reservationAndCancellationSettings.cancellationCreditDeduction')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
            </>
          )}

          <Input.Wrapper label='Auto-Charge Fee for No Shows'>
            <SimpleGrid cols={2}>
              <MoneyField
                country={country}
                min={0}
                {...form.getInputProps('reservationAndCancellationSettings.noShowFee')}
                disabled
              />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper>
            <SimpleGrid cols={1}>
              <Switch
                my='xs'
                label='Deduction of credit for No Shows'
                checked={form.values?.reservationAndCancellationSettings?.noShowCreditDeduction || false}
                disabled
                {...form.getInputProps('reservationAndCancellationSettings.noShowCreditDeduction')}
              />
            </SimpleGrid>
          </Input.Wrapper>
        </Stack>
      </ExpandableSection>
    </Stack>
  );
};

export default DetailsView;
